import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ColumnDef, type OnChangeFn, type SortingState } from '@tanstack/react-table';
import { useQuery } from 'convex/react';
import { api } from '@/lib/api';
import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/ui/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ServiceStatusBadge } from '@/components/features/operator/service-status-badge';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { ServiceCalendarLegend } from '@/components/features/services/ServiceCalendarLegend';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from '@/template/lib/toast';
import { CreateServiceDialog } from '@/components/features/operator/create-service-dialog';
import { TablePagination } from '@/components/ui/table-pagination';
import type { ServiceStatus } from '@/types/service-status';
import { type ServiceType } from '@/types/validators';
import { getOperatorServicesPageCursor } from '@/lib/operator-services-list';
import { type OperatorServiceSortOption, type OperatorServiceListItem, type OperatorServiceTimeFilter } from '@/types/operator-service';
import { mapSortingToSortKey, type SortMapping } from '@/lib/data-table-state';
import { useTranslation } from 'react-i18next';
import { endOfBerlinDay, formatBerlinDateTime, pickerDateToBerlinReference, startOfBerlinDay } from '@/lib/berlin-time';

// Lazy-load the calendar (react-big-calendar + lodash, ~250 kB) so it only
// downloads when the operator switches to the calendar view.
const ServiceCalendarView = React.lazy(() =>
  import('@/components/features/services/service-calendar-view').then((m) => ({
    default: m.ServiceCalendarView,
  }))
);

/**
 * Get service type label
 */
const getServiceTypeLabel = (type: ServiceType, t: (key: string) => string): string => {
  const labels: Record<ServiceType, string> = {
    fahrdienst: t('servicesList.serviceTypes.fahrdienst'),
    sitzdienst: t('servicesList.serviceTypes.sitzdienst'),
    kombidienst: t('servicesList.serviceTypes.kombidienst'),
    hintergrunddienst: t('servicesList.serviceTypes.hintergrunddienst'),
  };
  return labels[type] || type;
};

/**
 * Get service type badge classes (OKLCH theme tokens)
 */
const getServiceTypeBadgeClasses = (type: ServiceType): string => {
  const classes: Record<ServiceType, string> = {
    fahrdienst: 'bg-service-type-fahrdienst text-service-type-fahrdienst-foreground border-service-type-fahrdienst-border',
    sitzdienst: 'bg-service-type-sitzdienst text-service-type-sitzdienst-foreground border-service-type-sitzdienst-border',
    kombidienst: 'bg-service-type-kombidienst text-service-type-kombidienst-foreground border-service-type-kombidienst-border',
    hintergrunddienst: 'bg-service-type-hintergrunddienst text-service-type-hintergrunddienst-foreground border-service-type-hintergrunddienst-border',
  };
  return classes[type] || '';
};

/**
 * Empty state component
 */
const EmptyState = ({ t }: { t: (key: string) => string }): React.JSX.Element => (
  <div className='py-16 text-center'>
    <Icons.Inbox className='size-12 text-muted-foreground mx-auto mb-3' />
    <p className='text-muted-foreground text-sm'>{t('servicesList.empty.title')}</p>
    <p className='text-muted-foreground text-xs mt-1'>{t('servicesList.empty.description')}</p>
  </div>
);

const serviceSortColumns = ['startDate', 'serviceType', 'status', 'ownerName', 'city'] as const;
type ServiceSortColumn = (typeof serviceSortColumns)[number];
const serviceSortMapping: SortMapping<ServiceSortColumn, OperatorServiceSortOption> = {
  startDate: { asc: 'date_asc', desc: 'date_desc' },
  serviceType: { asc: 'type', desc: 'type' },
  status: { asc: 'status', desc: 'status' },
  ownerName: { asc: 'provider', desc: 'provider' },
  city: { asc: 'city', desc: 'city' },
};

const sortOptionToSorting = (sortBy: OperatorServiceSortOption): SortingState => {
  switch (sortBy) {
    case 'date_desc':
      return [{ id: 'startDate', desc: true }];
    case 'type':
      return [{ id: 'serviceType', desc: false }];
    case 'status':
      return [{ id: 'status', desc: false }];
    case 'provider':
      return [{ id: 'ownerName', desc: false }];
    case 'city':
      return [{ id: 'city', desc: false }];
    case 'date_asc':
      return [{ id: 'startDate', desc: false }];
  }
};

/**
 * Operator Services Management Page
 * Full CRUD operations for all services on the platform
 */
const OperatorServicesPage = () => {
  const { t, i18n } = useTranslation('operator');
  const navigate = useNavigate();

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>(() => {
    const saved = localStorage.getItem('operatorServicesViewMode');
    return (saved as 'list' | 'calendar') || 'list';
  });

  // Filters state
  const [statusFilter, setStatusFilter] = useState<ServiceStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<ServiceType | 'all'>('all');
  const [cityFilter, setCityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter] = useState('');
  const [startDateFrom, setStartDateFrom] = useState<Date | undefined>(undefined);
  const [startDateTo, setStartDateTo] = useState<Date | undefined>(undefined);
  const [applicationCountFilter, setApplicationCountFilter] = useState<'all' | '0' | '1-2' | '3-5' | '6+'>('all');
  // DA-474: Filter for services with manual service area
  const [manualServiceAreaFilter, setManualServiceAreaFilter] = useState<'all' | 'manual' | 'standard'>('all');
  const [timeFilter, setTimeFilter] = useState<OperatorServiceTimeFilter>('upcoming');

  // Sorting state with localStorage persistence
  const [sortBy, setSortBy] = useState<OperatorServiceSortOption>(() => {
    const saved = localStorage.getItem('operatorServicesSortBy');
    return (saved as OperatorServiceSortOption) || 'date_asc';
  });
  const serviceSorting = React.useMemo(() => sortOptionToSorting(sortBy), [sortBy]);

  // Page size state
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('operatorServicesPageSize');
    return saved ? Number(saved) : 20;
  });

  // Page state resets automatically when filters change.
  const [paginationState, setPaginationState] = useState<{
    currentPage: number;
    filterKey: string;
  }>({
    currentPage: 1,
    filterKey: '',
  });

  // Create service dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Date validation effect
  React.useEffect(() => {
    if (startDateFrom && startDateTo && startDateFrom > startDateTo) {
      toast.error(t('servicesList.toasts.invalidDateRange'));
    }
  }, [startDateFrom, startDateTo, t]);

  // Convert application count filter to min/max parameters
  const getApplicationCountParams = () => {
    switch (applicationCountFilter) {
      case '0':
        return { minApplications: 0, maxApplications: 0 };
      case '1-2':
        return { minApplications: 1, maxApplications: 2 };
      case '3-5':
        return { minApplications: 3, maxApplications: 5 };
      case '6+':
        return { minApplications: 6 };
      default:
        return {};
    }
  };

  // DA-360: Create filterKey to detect filter changes and auto-reset pagination
  const filterKey = JSON.stringify({
    statusFilter,
    typeFilter,
    cityFilter,
    searchQuery,
    startDateFrom: startDateFrom?.getTime(),
    startDateTo: startDateTo?.getTime(),
    applicationCountFilter,
    timeFilter,
    manualServiceAreaFilter, // DA-474
    sortBy,
    pageSize, // Include page size in filterKey to reset on change
  });

  // Validate pagination state against filterKey - auto-reset if filters changed
  const currentPage = paginationState.filterKey === filterKey ? paginationState.currentPage : 1;
  const cursor = getOperatorServicesPageCursor(currentPage, pageSize);

  // Build query filter args
  const countQueryArgs = {
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(typeFilter !== 'all' && { serviceType: typeFilter }),
    ...(cityFilter && { city: cityFilter }),
    ...(searchQuery && { searchQuery }),
    ...(startDateFrom && { startDateFrom: startOfBerlinDay(pickerDateToBerlinReference(startDateFrom)) }),
    ...(startDateTo && { startDateTo: endOfBerlinDay(pickerDateToBerlinReference(startDateTo)) }),
    timeFilter,
    manualServiceAreaFilter,
    ...getApplicationCountParams(),
  };

  const listQueryArgs = {
    ...countQueryArgs,
    sortBy,
  };

  // Query services with filters
  const servicesData = useQuery(api.app.operatorServiceQueries.listAllServices, {
    paginationOpts: {
      numItems: pageSize,
      cursor: cursor,
    },
    ...listQueryArgs,
  });

  // Query total count for pagination UI
  const totalCount = useQuery(api.app.operatorServiceQueries.getServicesCount, countQueryArgs);

  const isLoading = servicesData === undefined;
  const services: OperatorServiceListItem[] = servicesData?.page || [];
  const hasNextPage = servicesData ? !servicesData.isDone : false;
  const hasPrevPage = currentPage > 1;
  const totalPages = totalCount !== undefined ? Math.ceil(totalCount / pageSize) : undefined;

  // Persist sort preference to localStorage
  React.useEffect(() => {
    localStorage.setItem('operatorServicesSortBy', sortBy);
  }, [sortBy]);

  // Persist view mode preference to localStorage
  React.useEffect(() => {
    localStorage.setItem('operatorServicesViewMode', viewMode);
  }, [viewMode]);

  // Persist page size preference to localStorage
  React.useEffect(() => {
    localStorage.setItem('operatorServicesPageSize', String(pageSize));
  }, [pageSize]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage === currentPage || newPage < 1) {
        return;
      }

      if (totalPages !== undefined && newPage > totalPages) {
        return;
      }

      setPaginationState({
        currentPage: newPage,
        filterKey,
      });
    },
    [currentPage, filterKey, totalPages]
  );

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    // Reset to page 1 when page size changes (filterKey change will handle this automatically)
  }, []);

  /**
   * Reset filters (DA-360: Updated to use pagination state)
   */
  const handleResetFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setCityFilter('');
    setSearchQuery('');
    setStartDateFrom(undefined);
    setStartDateTo(undefined);
    setApplicationCountFilter('all');
    setTimeFilter('upcoming');
    setManualServiceAreaFilter('all'); // DA-474
    setSortBy('date_asc');
    setPaginationState({ currentPage: 1, filterKey: '' });
  };

  /**
   * Format date
   */
  const formatDate = useCallback(
    (timestamp: number): string => {
      return formatBerlinDateTime(timestamp, i18n.language.startsWith('en') ? 'en-US' : 'de-DE');
    },
    [i18n.language]
  );

  const currencyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(i18n.language.startsWith('en') ? 'en-US' : 'de-DE', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2,
      }),
    [i18n.language]
  );

  const formatCompensation = useCallback(
    (service: OperatorServiceListItem): string => {
      if (service.compensationType === 'fixed' && service.minPrice !== undefined) {
        return t('servicesList.table.compensationPerHour', { amount: currencyFormatter.format(service.minPrice) });
      }

      if (service.compensationType === 'range' && service.minPrice !== undefined && service.maxPrice !== undefined) {
        return t('servicesList.table.compensationRangePerHour', {
          min: currencyFormatter.format(service.minPrice),
          max: currencyFormatter.format(service.maxPrice),
        });
      }

      return '–';
    },
    [currencyFormatter, t]
  );

  const handleServiceSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const nextSorting = typeof updater === 'function' ? updater(sortOptionToSorting(sortBy)) : updater;
      const nextSortBy = mapSortingToSortKey(nextSorting, serviceSortColumns, serviceSortMapping, 'date_asc');
      if (nextSortBy === sortBy) return;
      setSortBy(nextSortBy);
      setPaginationState({ currentPage: 1, filterKey: '' });
    },
    [sortBy]
  );

  const sortedServices = services;

  const serviceColumns = React.useMemo<ColumnDef<OperatorServiceListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'ownerCustomerNumber',
        id: 'ownerCustomerNumber',
        header: t('servicesList.table.columns.customerNumber'),
        enableSorting: false,
        cell: ({ row }) => <span className='font-mono text-xs'>{row.original.ownerCustomerNumber || '–'}</span>,
      },
      {
        accessorKey: 'applicationCount',
        id: 'applicationCount',
        header: t('servicesList.table.columns.applications'),
        enableSorting: false,
        cell: ({ row }) => (
          <Badge variant={row.original.applicationCount === 0 ? 'destructive' : row.original.applicationCount <= 2 ? 'warning' : 'success'} className='tabular-nums'>
            {row.original.applicationCount}
          </Badge>
        ),
      },
      {
        accessorKey: 'status',
        id: 'status',
        header: t('servicesList.table.columns.status'),
        enableDescSorting: false,
        cell: ({ row }) => (
          <div className='flex items-center gap-1.5'>
            <ServiceStatusBadge status={row.original.status} />
            {(row.original.deletionRequestStatus === 'pending' || (row.original.deletionRequestStatus === undefined && row.original.deletionRequested)) && (
              <Badge
                variant='outline'
                className='text-xs bg-warning/10 border-warning text-warning-foreground'
                title={t('servicesList.table.deletionRequestedTitle', {
                  reason: row.original.deletionReason || t('servicesList.table.deletionRequestedNoReason'),
                })}
              >
                <Icons.AlertTriangle className='size-3 mr-1' />
                {t('servicesList.table.deletionRequested')}
              </Badge>
            )}
            {row.original.manualServiceArea && (
              <Badge variant='outline' className='text-xs bg-warning/10 border-warning text-warning-foreground' title={t('servicesList.table.manualTitle', { name: row.original.manualServiceArea })}>
                <Icons.AlertTriangle className='size-3 mr-1' />
                {t('servicesList.table.manual')}
              </Badge>
            )}
            {!row.original.serviceAreaId && !row.original.manualServiceArea && (
              <Badge variant='outline' className='text-xs bg-warning/10 border-warning text-warning-foreground' title={t('servicesList.table.incompleteTitle')}>
                <Icons.AlertTriangle className='size-3 mr-1' />
                {t('servicesList.table.incomplete')}
              </Badge>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'ownerName',
        id: 'ownerName',
        header: t('servicesList.table.columns.provider'),
        enableDescSorting: false,
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <span className='text-sm'>{row.original.ownerName}</span>
            <span className='text-xs text-muted-foreground'>{row.original.ownerEmail}</span>
          </div>
        ),
      },
      {
        id: 'city',
        accessorFn: (service) => service.location.city,
        header: t('servicesList.table.columns.location'),
        enableDescSorting: false,
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <span className='text-sm'>{row.original.location.city}</span>
            {row.original.location.state && <span className='text-xs text-muted-foreground'>{row.original.location.state}</span>}
          </div>
        ),
      },
      {
        accessorKey: 'startDate',
        id: 'startDate',
        header: t('servicesList.table.columns.startDate'),
        cell: ({ row }) => <span className='text-sm'>{formatDate(row.original.startDate)}</span>,
      },
      {
        id: 'compensation',
        header: t('servicesList.table.columns.compensation'),
        enableSorting: false,
        cell: ({ row }) => <span className='text-sm whitespace-nowrap tabular-nums'>{formatCompensation(row.original)}</span>,
      },
      {
        accessorKey: 'serviceType',
        id: 'serviceType',
        header: t('servicesList.table.columns.type'),
        enableDescSorting: false,
        cell: ({ row }) => <Badge className={getServiceTypeBadgeClasses(row.original.serviceType)}>{getServiceTypeLabel(row.original.serviceType, t)}</Badge>,
      },
      {
        id: 'actions',
        header: t('servicesList.table.columns.actions'),
        enableSorting: false,
        meta: { headerClassName: 'text-right', cellClassName: 'text-right' },
        cell: ({ row }) => (
          <Button
            variant='ghost'
            size='sm'
            onClick={() => {
              navigate({
                to: '/operators/services/$serviceId',
                params: { serviceId: row.original._id },
              });
            }}
          >
            {t('servicesList.actions.details')}
            <Icons.ChevronRight className='size-4 ml-2' />
          </Button>
        ),
      },
    ],
    [formatCompensation, formatDate, navigate, t]
  );

  return (
    <div className='container mx-auto py-8 space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>{t('servicesList.title')}</h1>
          <p className='text-muted-foreground mt-1'>{t('servicesList.subtitle')}</p>
        </div>
        <div className='flex items-center gap-2'>
          {/* View Toggle */}
          <div className='flex items-center border rounded-md'>
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size='sm' onClick={() => setViewMode('list')} className='rounded-r-none'>
              <Icons.List className='size-4 mr-2' />
              {t('servicesList.view.list')}
            </Button>
            <Button variant={viewMode === 'calendar' ? 'default' : 'ghost'} size='sm' onClick={() => setViewMode('calendar')} className='rounded-l-none'>
              <Icons.Calendar className='size-4 mr-2' />
              {t('servicesList.view.calendar')}
            </Button>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Icons.Plus className='size-4 mr-2' />
            {t('servicesList.actions.create')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>{t('servicesList.filters.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            {/* Search - Full Width */}
            <div>
              <Input
                placeholder={t('servicesList.filters.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // DA-360: No manual reset needed - filterKey handles it automatically
                }}
                className='w-full'
              />
            </div>

            {/* Filters Grid */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4'>
              {/* Status Filter */}
              <Select
                value={statusFilter}
                onValueChange={(value: string | null) => {
                  setStatusFilter(value as ServiceStatus | 'all');
                  // DA-360: No manual reset needed - filterKey handles it automatically
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('servicesList.filters.statusPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('servicesList.filters.status.all')}</SelectItem>
                  <SelectItem value='open'>{t('servicesList.filters.status.open')}</SelectItem>
                  <SelectItem value='pending_payment'>{t('servicesList.filters.status.pendingPayment')}</SelectItem>
                  <SelectItem value='pending_contract'>{t('servicesList.filters.status.pendingContract')}</SelectItem>
                  <SelectItem value='filled'>{t('servicesList.filters.status.filled')}</SelectItem>
                  <SelectItem value='performed'>{t('servicesList.filters.status.performed')}</SelectItem>
                  <SelectItem value='completed'>{t('servicesList.filters.status.completed')}</SelectItem>
                  <SelectItem value='cancelled'>{t('servicesList.filters.status.cancelled')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Service Type Filter */}
              <Select
                value={typeFilter}
                onValueChange={(value: string | null) => {
                  setTypeFilter(value as ServiceType | 'all');
                  // DA-360: No manual reset needed - filterKey handles it automatically
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('servicesList.filters.typePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('servicesList.filters.types.all')}</SelectItem>
                  <SelectItem value='fahrdienst'>{t('servicesList.serviceTypes.fahrdienst')}</SelectItem>
                  <SelectItem value='sitzdienst'>{t('servicesList.serviceTypes.sitzdienst')}</SelectItem>
                  <SelectItem value='kombidienst'>{t('servicesList.serviceTypes.kombidienst')}</SelectItem>
                  <SelectItem value='hintergrunddienst'>{t('servicesList.serviceTypes.hintergrunddienst')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Application Count Filter */}
              <Select
                value={applicationCountFilter}
                onValueChange={(value: string | null) => {
                  setApplicationCountFilter(value as 'all' | '0' | '1-2' | '3-5' | '6+');
                  // DA-360: No manual reset needed - filterKey handles it automatically
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('servicesList.filters.applicationsPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('servicesList.filters.applications.all')}</SelectItem>
                  <SelectItem value='0'>{t('servicesList.filters.applications.none')}</SelectItem>
                  <SelectItem value='1-2'>{t('servicesList.filters.applications.few')}</SelectItem>
                  <SelectItem value='3-5'>{t('servicesList.filters.applications.medium')}</SelectItem>
                  <SelectItem value='6+'>{t('servicesList.filters.applications.many')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Time Filter */}
              <Select
                value={timeFilter}
                onValueChange={(value: string | null) => {
                  setTimeFilter((value as OperatorServiceTimeFilter | null) ?? 'upcoming');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('servicesList.filters.timePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='upcoming'>{t('servicesList.filters.time.upcoming')}</SelectItem>
                  <SelectItem value='past'>{t('servicesList.filters.time.past')}</SelectItem>
                  <SelectItem value='all'>{t('servicesList.filters.time.all')}</SelectItem>
                </SelectContent>
              </Select>

              {/* DA-474: Manual Service Area Filter */}
              <Select
                value={manualServiceAreaFilter}
                onValueChange={(value: string | null) => {
                  setManualServiceAreaFilter(value as 'all' | 'manual' | 'standard');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('servicesList.filters.serviceAreaPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('servicesList.filters.serviceArea.all')}</SelectItem>
                  <SelectItem value='manual'>{t('servicesList.filters.serviceArea.manual')}</SelectItem>
                  <SelectItem value='standard'>{t('servicesList.filters.serviceArea.standard')}</SelectItem>
                </SelectContent>
              </Select>

              {/* City Filter */}
              <Input
                placeholder={t('servicesList.filters.cityPlaceholder')}
                value={cityFilter}
                onChange={(e) => {
                  setCityFilter(e.target.value);
                  // DA-360: No manual reset needed - filterKey handles it automatically
                }}
              />
            </div>

            {/* Date Range Filters */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              <div className='space-y-2'>
                <label htmlFor='start-date-from' className='text-sm font-medium text-foreground'>
                  {t('servicesList.filters.startDateFrom')}
                </label>
                <DatePicker
                  value={startDateFrom}
                  onChange={(date) => {
                    setStartDateFrom(date);
                    // DA-360: No manual reset needed - filterKey handles it automatically
                  }}
                />
              </div>

              <div className='space-y-2'>
                <label htmlFor='start-date-to' className='text-sm font-medium text-foreground'>
                  {t('servicesList.filters.startDateTo')}
                </label>
                <DatePicker
                  value={startDateTo}
                  onChange={(date) => {
                    setStartDateTo(date);
                    // DA-360: No manual reset needed - filterKey handles it automatically
                  }}
                />
              </div>

              {/* Reset Button */}
              <div className='space-y-2'>
                <label className='text-sm font-medium text-transparent select-none'>{t('servicesList.filters.actionsLabel')}</label>
                <Button variant='outline' onClick={handleResetFilters} className='w-full'>
                  <Icons.X className='size-4 mr-2' />
                  {t('servicesList.actions.reset')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Content */}
      {viewMode === 'calendar' ? (
        <div className='grid grid-cols-1 lg:grid-cols-4 gap-6'>
          <div className='lg:col-span-3'>
            <React.Suspense
              fallback={
                <Card>
                  <CardContent className='p-8'>
                    <div className='flex items-center justify-center' style={{ height: '600px' }}>
                      <Icons.Loader className='h-6 w-6 animate-spin text-muted-foreground' />
                    </div>
                  </CardContent>
                </Card>
              }
            >
              <ServiceCalendarView
                {...(statusFilter !== 'all' ? { statusFilter: statusFilter as ServiceStatus } : {})}
                {...(typeFilter !== 'all' ? { serviceTypeFilter: typeFilter as ServiceType } : {})}
                {...(cityFilter ? { cityFilter } : {})}
                {...(stateFilter ? { stateFilter } : {})}
              />
            </React.Suspense>
          </div>
          <div className='lg:col-span-1'>
            <ServiceCalendarLegend />
          </div>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <CardTitle>{t('servicesList.table.title')}</CardTitle>
              {!isLoading && sortedServices.length > 0 && <Badge variant='secondary'>{t('servicesList.table.results', { count: sortedServices.length })}</Badge>}
            </div>
          </CardHeader>
          <CardContent className='p-4'>
            <div className='max-h-[600px] overflow-y-auto'>
              <DataTable
                columns={serviceColumns}
                data={sortedServices}
                sorting={serviceSorting}
                onSortingChange={handleServiceSortingChange}
                manualSorting
                isLoading={isLoading}
                loadingRowCount={10}
                emptyState={<EmptyState t={t} />}
                getRowId={(service) => service._id}
                headerClassName='sticky top-0 z-10 bg-background'
              />
            </div>
            {!isLoading && sortedServices.length > 0 && (
              <>
                {/* Pagination */}
                <TablePagination
                  currentPage={currentPage}
                  {...(totalPages !== undefined && { totalPages })}
                  {...(totalCount !== undefined && { totalItems: totalCount })}
                  pageSize={pageSize}
                  pageSizeOptions={[10, 20, 50, 100]}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  isLoading={isLoading}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Service Dialog */}
      <CreateServiceDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={(serviceId) => {
          // Navigate to the newly created service details page
          navigate({
            to: '/operators/services/$serviceId',
            params: { serviceId },
          });
        }}
      />
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/services/')({
  component: OperatorServicesPage,
});
